import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { StepperConfig, StepperSegment } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { mapTypographyStyles, mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { useFontOverrides } from '@modules/font-config/context/FontConfigContext';

import {
  CircleLayout,
  ImageContainerFirstLayout,
  ChartContainerFirstLayout,
  LineProgressBar,
} from './kinds';

export interface ProgressBarProps {
  stepper: StepperConfig | undefined;
  progress: number;
  locale: string;
  variables?: TemplateVariables;
  previewMode?: PreviewMode;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  animationDuration?: number;
  animationKey?: number;
  isAnimating?: boolean;
  currentSegmentIndex?: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function findCurrentSegment(
  items: StepperSegment[] | undefined,
  progress: number,
): { segment: StepperSegment | null; index: number } {
  if (!items || items.length === 0) {
    return { segment: null, index: -1 };
  }

  const foundIndex = items.findIndex(item => {
    const from = item.progressStart ?? 0;
    const to = item.progressEnd ?? 100;
    return progress >= from && progress < to;
  });

  if (foundIndex !== -1) {
    return { segment: items[foundIndex], index: foundIndex };
  }

  const lastIndex = items.length - 1;
  return { segment: items[lastIndex], index: lastIndex };
}

export const ProgressBar = memo(function ProgressBar({
  stepper,
  progress,
  locale,
  variables,
  previewMode,
  className,
  'data-testid': testId,
  editPath,
  animationDuration,
  animationKey,
  isAnimating,
  currentSegmentIndex: externalSegmentIndex,
}: ProgressBarProps): React.ReactElement | null {
  const fontOverrides = useFontOverrides();

  if (!stepper) {
    return null;
  }

  const { items, kind, label, layout } = stepper;

  const clampedProgress = clamp(progress, 0, 100);

  const { segment: calculatedSegment, index: calculatedIndex } = findCurrentSegment(items, clampedProgress);

  const currentSegmentIndex = externalSegmentIndex !== undefined ? externalSegmentIndex : calculatedIndex;
  const currentSegment = externalSegmentIndex !== undefined && items?.[externalSegmentIndex]
    ? items[externalSegmentIndex]
    : calculatedSegment;

  // V2: use mapTypographyStyles instead of manual LabelBlock mapping
  const labelStyle: CSSProperties = mapTypographyStyles(label?.appearance, fontOverrides);
  const hasLabelStyle = Object.keys(labelStyle).length > 0;

  // V2: use mapFrameLayoutStyles instead of manual box padding
  const containerStyle: CSSProperties = mapFrameLayoutStyles(layout);

  const kindProps = {
    stepper,
    progress: clampedProgress,
    locale,
    currentSegment,
    currentSegmentIndex,
    labelStyle,
    hasLabelStyle,
    containerStyle,
    variables,
    previewMode,
    className,
    testId,
    editPath,
    animationDuration,
    animationKey,
    isAnimating,
  };

  switch (kind) {
    case 'orbital':
      return <CircleLayout {...kindProps} />;

    case 'imageContainerFirst':
      return <ImageContainerFirstLayout {...kindProps} />;

    case 'chartContainerFirst':
      return <ChartContainerFirstLayout {...kindProps} />;

    default:
      return <LineProgressBar {...kindProps} />;
  }
});

ProgressBar.displayName = 'ProgressBar';
